/**
 * Spooky Shared Terminal - Client
 * Using PixiJS-based GPU-accelerated terminal renderer
 */

import { TerminalRenderer, Cell, Cursor, PixelRenderer, PixelRegion, HybridModeRenderer, HybridPixelRegionDef } from './renderer.js';
import { AudioEngine, StreamingAudioPlayer } from './audio.js';
import { OPL2Synthesizer, createOPL2Synthesizer } from './opl2-synth.js';
import { ADPCMPlayer, createADPCMPlayer } from './adpcm-player.js';
import { CLIENT, RECONNECT, CRT_DECAY, ADPCM } from './config.js';
import { AUDIO } from './audio-config.js';

import type {
  ServerMessage,
  FullSyncMessage,
  DeltaMessage,
  AudioMessage,
  PowerStateMessage,
  CRTLevelMessage,
  LEDStateMessage,
  ClientMessage,
  KeyInputMessage,
  KeyReleaseMessage,
  KeysReleaseAllMessage,
  MouseInputMessage,
  PixelModeMessage,
  PixelDeltaMessage,
  PixelFullSyncMessage,
  HybridModeMessage,
  HybridPixelDeltaMessage,
  HybridPixelFullSyncMessage,
  OPLModeMessage,
  OPLDataMessage,
  ADPCMModeMessage,
  ADPCMDataMessage,
  DOSBoxAudioMessage,
  CellChange,
} from '../shared/protocol.js';

import type {
  CompressedCells,
  MinimalCell,
} from '../shared/types.js';

/**
 * Terminal client class
 */
class TerminalClient {
  private renderer: TerminalRenderer | null = null;
  private pixelRenderer: PixelRenderer | null = null;
  private hybridRenderer: HybridModeRenderer | null = null;
  private socket: WebSocket | null = null;
  private _clientId: string = '';
  private reconnectAttempts: number = 0;
  private readonly maxReconnectAttempts: number = RECONNECT.MAX_ATTEMPTS;
  private _reconnectTimeout: NodeJS.Timeout | null = null;
  private pingInterval: NodeJS.Timeout | null = null;

  // Audio engine for PC speaker style sounds
  private audio: AudioEngine;
  private audioInitialized: boolean = false;

  // OPL2 FM synthesizer for retro stream audio
  private oplSynth: OPL2Synthesizer | null = null;

  // ADPCM player for compressed waveform audio from stream
  private adpcmPlayer: ADPCMPlayer | null = null;

  // DOSBox audio player for js-dos emulation (pinball, etc.)
  private dosboxAudio: StreamingAudioPlayer | null = null;

  // Pixel mode state
  private inPixelMode: boolean = false;
  private inHybridMode: boolean = false;
  private pointerLocked: boolean = false;

  // DOM elements
  private statusElement: HTMLElement;
  private powerButton: HTMLElement;
  private powerOffScreen: HTMLElement;
  private screenContainer: HTMLElement;
  private ledPower: HTMLElement;
  private ledHdd: HTMLElement;
  private ledNetwork: HTMLElement;
  private volumeKnob: HTMLElement;
  private volumeOSD: HTMLElement;
  private volumeOSDTimeout: ReturnType<typeof setTimeout> | null = null;

  // State
  private powerOn: boolean = true;
  private crtDecay: number = 0;
  private initialSyncDone: boolean = false;

  // Event listener references (for cleanup)
  private boundKeydownHandler: ((event: KeyboardEvent) => void) | null = null;
  private boundKeyupHandler: ((event: KeyboardEvent) => void) | null = null;
  private boundClickHandler: (() => void) | null = null;
  private boundPowerClickHandler: (() => void) | null = null;
  private boundMouseMoveHandler: ((event: MouseEvent) => void) | null = null;
  private boundMouseDownHandler: ((event: MouseEvent) => void) | null = null;
  private boundMouseUpHandler: ((event: MouseEvent) => void) | null = null;
  private boundPointerLockChangeHandler: (() => void) | null = null;
  private boundVisibilityChangeHandler: (() => void) | null = null;
  private boundBlurHandler: (() => void) | null = null;
  private boundFocusHandler: (() => void) | null = null;

  // Track currently pressed keys to prevent stuck keys on blur/alt-tab
  private pressedKeys: Set<string> = new Set();
  private isTabActive: boolean = true;

  // Mouse move throttling (~30 messages/sec)
  private lastMouseMoveTime: number = 0;
  private readonly MOUSE_THROTTLE_MS = 33; // ~30 msgs/sec

  // Screen buffer for rendering
  private readonly screenWidth: number = CLIENT.SCREEN_WIDTH;
  private readonly screenHeight: number = CLIENT.SCREEN_HEIGHT;
  private screenBuffer: MinimalCell[][] = [];

  constructor() {
    // Initialize audio engine
    this.audio = new AudioEngine();

    // Initialize DOM elements
    this.statusElement = document.getElementById('connectionStatus')!;
    this.powerButton = document.getElementById('powerButton')!;
    this.powerOffScreen = document.getElementById('powerOffScreen')!;
    this.screenContainer = document.querySelector('.screen-container')!;
    this.ledPower = document.getElementById('ledPower')!;
    this.ledHdd = document.getElementById('ledHdd')!;
    this.ledNetwork = document.getElementById('ledNetwork')!;
    this.volumeKnob = document.getElementById('volumeKnob')!;
    this.volumeOSD = document.getElementById('volumeOSD')!;

    // Initialize volume knob visual to default position
    this.initVolumeKnob();

    // Initialize screen buffer
    this.initScreenBuffer();
  }

  /**
   * Initialize the terminal renderer and set up event handlers
   */
  async init(container: HTMLElement): Promise<void> {
    // Initialize PixiJS renderer
    this.renderer = new TerminalRenderer(container);
    await this.renderer.init();

    // Initialize pixel renderer (hidden by default)
    this.pixelRenderer = new PixelRenderer(container);

    // Initialize hybrid mode renderer
    this.hybridRenderer = new HybridModeRenderer(container);

    // Set up event handlers
    this.setupEventHandlers();

    // Connect to server
    this.connect();
  }

  /**
   * Initialize screen buffer
   */
  private initScreenBuffer(): void {
    this.screenBuffer = [];
    for (let y = 0; y < this.screenHeight; y++) {
      const row: MinimalCell[] = [];
      for (let x = 0; x < this.screenWidth; x++) {
        row.push({ ch: ' ', fg: CLIENT.DEFAULT_FG_COLOR, bg: CLIENT.DEFAULT_BG_COLOR });
      }
      this.screenBuffer.push(row);
    }
  }

  /**
   * Initialize audio on first user interaction
   */
  private initAudioOnInteraction(): void {
    if (!this.audioInitialized) {
      this.audioInitialized = this.audio.init();
    }
  }

  /**
   * Initialize the volume knob with drag-to-rotate interaction.
   * Rotation range: -135deg (min/mute) to +135deg (max).
   * Default 80% volume = rotation of 81deg.
   */
  private initVolumeKnob(): void {
    const knob = this.volumeKnob;
    if (!knob) return;

    // Map volume (0-1) to rotation (-135 to +135 degrees)
    const volumeToRotation = (v: number) => -135 + v * 270;
    const rotationToVolume = (deg: number) => Math.max(0, Math.min(1, (deg + 135) / 270));

    // Set initial rotation from default volume
    let currentVolume: number = AUDIO.DEFAULT_MASTER_VOLUME;
    let currentRotation: number = volumeToRotation(currentVolume);
    knob.style.transform = `rotate(${currentRotation}deg)`;

    let dragging = false;
    let startY = 0;
    let startRotation = 0;

    const onMouseDown = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      this.initAudioOnInteraction();
      dragging = true;
      startY = e.clientY;
      startRotation = currentRotation;
      knob.classList.add('dragging');
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!dragging) return;
      // Drag up = louder, drag down = quieter. 150px drag = full range.
      const deltaY = startY - e.clientY;
      const newRotation = Math.max(-135, Math.min(135, startRotation + deltaY * 1.8));
      currentRotation = newRotation;
      currentVolume = rotationToVolume(newRotation);
      knob.style.transform = `rotate(${currentRotation}deg)`;
      this.audio.setVolume(currentVolume);
      this.showVolumeOSD(currentVolume);
    };

    const onMouseUp = () => {
      dragging = false;
      knob.classList.remove('dragging');
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    knob.addEventListener('mousedown', onMouseDown);

    // Scroll wheel support: scroll up = louder, down = quieter
    knob.addEventListener('wheel', (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      this.initAudioOnInteraction();
      const step = e.deltaY < 0 ? 13.5 : -13.5; // 5% per scroll tick
      currentRotation = Math.max(-135, Math.min(135, currentRotation + step));
      currentVolume = rotationToVolume(currentRotation);
      knob.style.transform = `rotate(${currentRotation}deg)`;
      this.audio.setVolume(currentVolume);
      this.showVolumeOSD(currentVolume);
    }, { passive: false });
  }

  /**
   * Show the volume OSD with the given volume level (0-1) and auto-hide after 1.5s
   */
  private showVolumeOSD(volume: number): void {
    const osd = this.volumeOSD;
    if (!osd) return;

    // Update segments: 10 segments, each represents 10%
    const segments = osd.querySelectorAll('.volume-osd-segment');
    const activeCount = Math.round(volume * 10);
    segments.forEach((seg, i) => {
      seg.classList.toggle('active', i < activeCount);
    });

    // Show
    osd.classList.add('visible');

    // Clear previous hide timer
    if (this.volumeOSDTimeout) {
      clearTimeout(this.volumeOSDTimeout);
    }

    // Auto-hide after 1.5 seconds
    this.volumeOSDTimeout = setTimeout(() => {
      osd.classList.remove('visible');
      this.volumeOSDTimeout = null;
    }, 1500);
  }

  /**
   * Set up event handlers
   */
  private setupEventHandlers(): void {
    // Keyboard input - listen on document for global input
    this.boundKeydownHandler = (event: KeyboardEvent) => {
      // Initialize audio on first keypress (Web Audio requires user gesture)
      this.initAudioOnInteraction();

      if (!this.powerOn || !this.socket) return;

      // Ignore modifier-only keypresses (except in pixel mode)
      if (this.isModifierOnly(event)) {
        return;
      }

      // Prevent default browser behavior for terminal keys
      if (event.ctrlKey || event.altKey || this.isTerminalKey(event.key)) {
        event.preventDefault();
      }

      // Track this key as pressed (for stuck key prevention)
      this.pressedKeys.add(event.code);

      const message: KeyInputMessage = {
        type: 'input:key',
        key: this.getKeyValue(event),
        code: event.code,
        modifiers: {
          ctrl: event.ctrlKey,
          alt: event.altKey,
          shift: event.shiftKey,
          meta: event.metaKey,
        },
        repeat: event.repeat,
      };

      this.send(message);
    };
    document.addEventListener('keydown', this.boundKeydownHandler);

    // Key release - for programs that need held key support (e.g., pinball flippers)
    this.boundKeyupHandler = (event: KeyboardEvent) => {
      if (!this.powerOn || !this.socket) return;

      // Ignore modifier-only releases (except in pixel mode)
      if (this.isModifierOnly(event)) return;

      // Untrack this key
      this.pressedKeys.delete(event.code);

      const message: KeyReleaseMessage = {
        type: 'input:keyup',
        code: event.code,
      };
      this.send(message);
    };
    document.addEventListener('keyup', this.boundKeyupHandler);

    // Power button
    this.boundPowerClickHandler = () => {
      this.initAudioOnInteraction();
      this.send({ type: 'power', action: this.powerOn ? 'off' : 'on' });
    };
    this.powerButton.addEventListener('click', this.boundPowerClickHandler);

    // Click to focus and init audio
    this.boundClickHandler = () => {
      this.initAudioOnInteraction();
      this.renderer?.focus();
    };
    document.addEventListener('click', this.boundClickHandler);

    // Mouse movement tracking for terminal programs
    this.boundMouseMoveHandler = (event: MouseEvent) => {
      if (!this.powerOn || !this.socket || !this.renderer) return;

      // Throttle mousemove messages to ~30/sec
      const now = performance.now();
      if (now - this.lastMouseMoveTime < this.MOUSE_THROTTLE_MS) return;
      this.lastMouseMoveTime = now;

      // When pointer is locked, send relative motion deltas
      if (this.pointerLocked && this.inPixelMode) {
        const message: MouseInputMessage = {
          type: 'input:mouse',
          x: event.movementX,
          y: event.movementY,
          button: event.buttons === 1 ? 0 : event.buttons === 2 ? 2 : event.buttons === 4 ? 1 : 3,
          action: 'move',
          isRelative: true,
        };
        this.send(message);
        return;
      }

      // Convert screen coordinates to terminal coordinates
      const termCoords = this.renderer.screenToTerminal(event.clientX, event.clientY);
      if (!termCoords) return;

      const message: MouseInputMessage = {
        type: 'input:mouse',
        x: termCoords.x,
        y: termCoords.y,
        button: event.buttons === 1 ? 0 : event.buttons === 2 ? 2 : event.buttons === 4 ? 1 : 3,
        action: 'move',
      };

      this.send(message);
    };
    document.addEventListener('mousemove', this.boundMouseMoveHandler);

    // Mouse down handler for clicks
    this.boundMouseDownHandler = (event: MouseEvent) => {
      if (!this.powerOn || !this.socket || !this.renderer) return;

      const canvas = document.getElementById('terminal');

      // In pixel mode, request pointer lock on click (for games like Raptor)
      if (this.inPixelMode && canvas && document.pointerLockElement !== canvas) {
        canvas.requestPointerLock();
      }

      // Convert screen coordinates to terminal coordinates
      const termCoords = this.renderer.screenToTerminal(event.clientX, event.clientY);
      if (!termCoords) return;

      const message: MouseInputMessage = {
        type: 'input:mouse',
        x: termCoords.x,
        y: termCoords.y,
        button: event.button, // 0=left, 1=middle, 2=right
        action: 'down',
      };

      this.send(message);
    };
    document.addEventListener('mousedown', this.boundMouseDownHandler);

    // Mouse up handler
    this.boundMouseUpHandler = (event: MouseEvent) => {
      if (!this.powerOn || !this.socket || !this.renderer) return;

      // Convert screen coordinates to terminal coordinates
      const termCoords = this.renderer.screenToTerminal(event.clientX, event.clientY);
      if (!termCoords) return;

      const message: MouseInputMessage = {
        type: 'input:mouse',
        x: termCoords.x,
        y: termCoords.y,
        button: event.button,
        action: 'up',
      };

      this.send(message);
    };
    document.addEventListener('mouseup', this.boundMouseUpHandler);

    // Pointer lock change handler (for DOSBox games with mouse)
    const canvas = document.getElementById('terminal');
    this.boundPointerLockChangeHandler = () => {
      this.pointerLocked = document.pointerLockElement === canvas;
    };
    document.addEventListener('pointerlockchange', this.boundPointerLockChangeHandler);

    // Visibility change handler - release all keys when tab becomes hidden
    this.boundVisibilityChangeHandler = () => {
      const container = document.querySelector('.crt-container') || document.body;
      if (document.hidden) {
        this.isTabActive = false;
        this.releaseAllKeys();
        container.classList.add('crt-paused');
      } else {
        this.isTabActive = true;
        // Only remove crt-paused if power is on (power-off keeps it paused)
        if (this.powerOn) {
          container.classList.remove('crt-paused');
        }
      }
    };
    document.addEventListener('visibilitychange', this.boundVisibilityChangeHandler);

    // Window blur handler - release all keys when window loses focus (alt-tab, etc.)
    this.boundBlurHandler = () => {
      this.releaseAllKeys();
    };
    window.addEventListener('blur', this.boundBlurHandler);

    // Window focus handler - just for tracking state
    this.boundFocusHandler = () => {
      this.isTabActive = true;
    };
    window.addEventListener('focus', this.boundFocusHandler);
  }

  /**
   * Clean up event listeners and resources
   */
  destroy(): void {
    // Remove event listeners
    if (this.boundKeydownHandler) {
      document.removeEventListener('keydown', this.boundKeydownHandler);
      this.boundKeydownHandler = null;
    }
    if (this.boundKeyupHandler) {
      document.removeEventListener('keyup', this.boundKeyupHandler);
      this.boundKeyupHandler = null;
    }
    if (this.boundClickHandler) {
      document.removeEventListener('click', this.boundClickHandler);
      this.boundClickHandler = null;
    }
    if (this.boundPowerClickHandler) {
      this.powerButton.removeEventListener('click', this.boundPowerClickHandler);
      this.boundPowerClickHandler = null;
    }
    if (this.boundMouseMoveHandler) {
      document.removeEventListener('mousemove', this.boundMouseMoveHandler);
      this.boundMouseMoveHandler = null;
    }
    if (this.boundMouseDownHandler) {
      document.removeEventListener('mousedown', this.boundMouseDownHandler);
      this.boundMouseDownHandler = null;
    }
    if (this.boundMouseUpHandler) {
      document.removeEventListener('mouseup', this.boundMouseUpHandler);
      this.boundMouseUpHandler = null;
    }
    if (this.boundPointerLockChangeHandler) {
      document.removeEventListener('pointerlockchange', this.boundPointerLockChangeHandler);
      this.boundPointerLockChangeHandler = null;
    }
    if (this.boundVisibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.boundVisibilityChangeHandler);
      this.boundVisibilityChangeHandler = null;
    }
    if (this.boundBlurHandler) {
      window.removeEventListener('blur', this.boundBlurHandler);
      this.boundBlurHandler = null;
    }
    if (this.boundFocusHandler) {
      window.removeEventListener('focus', this.boundFocusHandler);
      this.boundFocusHandler = null;
    }

    // Clear pressed keys
    this.pressedKeys.clear();

    // Stop ping interval
    this.stopPing();

    // Cancel reconnect timeout
    if (this._reconnectTimeout) {
      clearTimeout(this._reconnectTimeout);
      this._reconnectTimeout = null;
    }

    // Close socket
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }

    // Destroy renderers
    this.renderer?.destroy();
    this.renderer = null;
    this.pixelRenderer?.destroy();
    this.pixelRenderer = null;
    this.hybridRenderer?.destroy();
    this.hybridRenderer = null;

    // Destroy OPL synth
    this.oplSynth?.destroy();
    this.oplSynth = null;

    // Stop ADPCM player
    this.adpcmPlayer?.stop();
    this.adpcmPlayer = null;
  }

  /**
   * Check if the keypress is modifier-only (no actual character)
   * In pixel mode (DOSBox games), we need to send modifier keys like Control for firing
   */
  private isModifierOnly(event: KeyboardEvent): boolean {
    // In pixel mode (DOSBox games), send all keys including modifiers
    if (this.inPixelMode) {
      return false;
    }

    const modifierKeys = [
      'Control', 'Alt', 'Shift', 'Meta',
      'CapsLock', 'NumLock', 'ScrollLock',
      'ContextMenu', 'AltGraph',
    ];
    return modifierKeys.includes(event.key);
  }

  /**
   * Check if a key should be captured by terminal
   */
  private isTerminalKey(key: string): boolean {
    const terminalKeys = [
      'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
      'Tab', 'Escape', 'Backspace', 'Delete', 'Enter',
      'Home', 'End', 'PageUp', 'PageDown',
    ];
    return terminalKeys.includes(key);
  }

  /**
   * Release all currently pressed keys
   * Called on blur/visibility change to prevent stuck keys when alt-tabbing
   */
  private releaseAllKeys(): void {
    if (this.pressedKeys.size === 0) return;

    // Send a single "release all" message for efficiency
    const message: KeysReleaseAllMessage = {
      type: 'input:keys_release_all',
    };
    this.send(message);

    // Clear local tracking
    this.pressedKeys.clear();
  }

  /**
   * Get the key value for transmission
   */
  private getKeyValue(event: KeyboardEvent): string {
    // Handle special keys
    switch (event.key) {
      case 'Enter': return '\r';
      case 'Backspace': return '\x7f';
      case 'Tab': return '\t';
      case 'Escape': return '\x1b';
      case 'ArrowUp': return '\x1b[A';
      case 'ArrowDown': return '\x1b[B';
      case 'ArrowRight': return '\x1b[C';
      case 'ArrowLeft': return '\x1b[D';
      case 'Home': return '\x1b[H';
      case 'End': return '\x1b[F';
      case 'Delete': return '\x1b[3~';
      case 'PageUp': return '\x1b[5~';
      case 'PageDown': return '\x1b[6~';
    }

    // Handle Ctrl+key combinations
    if (event.ctrlKey && event.key.length === 1) {
      const code = event.key.toLowerCase().charCodeAt(0);
      if (code >= 97 && code <= 122) {
        return String.fromCharCode(code - 96); // Ctrl+A = 1, Ctrl+B = 2, etc.
      }
    }

    return event.key;
  }

  /**
   * Connect to WebSocket server
   * Supports deployment via:
   * - window.WS_URL: Full WebSocket URL override
   * - window.WS_PORT: Custom port (defaults to 3001 for dev, or same as HTTP for reverse proxy)
   * - Auto-detects ws:// vs wss:// based on page protocol
   */
  private connect(): void {
    // Close existing socket before creating new one (Issue 4)
    if (this.socket) {
      this.socket.onclose = null; // Prevent triggering reconnect
      this.socket.close();
      this.socket = null;
    }

    const wsUrl = this.buildWebSocketUrl();

    this.updateStatus('connecting');
    this.socket = new WebSocket(wsUrl);
    this.socket.binaryType = 'arraybuffer';

    this.socket.onopen = () => {
      this.reconnectAttempts = 0;
      this.updateStatus('connected');
      this.startPing();
    };

    this.socket.onclose = () => {
      this.updateStatus('disconnected');
      this.stopPing();
      this.socket = null; // Nullify socket reference (Issue 3)
      this.scheduleReconnect();
    };

    this.socket.onerror = (error) => {
      // Log more details about the error
      console.error('WebSocket error:', {
        type: (error as Event).type,
        target: (error as Event).target,
        error,
      });
    };

    this.socket.onmessage = (event) => {
      // Handle binary messages (DOSBox audio, pixel deltas)
      // binaryType is set to 'arraybuffer' so binary data arrives as ArrayBuffer directly
      if (event.data instanceof ArrayBuffer) {
        this.handleBinaryMessage(event.data);
        return;
      }
      this.handleMessage(event.data);
    };
  }

  /**
   * Build WebSocket URL based on environment
   * Priority: window.WS_URL > window.WS_PORT > auto-detect
   */
  private buildWebSocketUrl(): string {
    // Allow full URL override (e.g., for complex proxy setups)
    const wsUrlOverride = (window as unknown as { WS_URL?: string }).WS_URL;
    if (wsUrlOverride) {
      console.log(`WebSocket URL (override): ${wsUrlOverride}`);
      return wsUrlOverride;
    }

    // Determine protocol (wss for HTTPS, ws for HTTP)
    const isSecure = window.location.protocol === 'https:';
    const wsProtocol = isSecure ? 'wss:' : 'ws:';

    // Determine port
    const wsPortOverride = (window as unknown as { WS_PORT?: number | string }).WS_PORT;
    let port: string;

    if (wsPortOverride) {
      // Explicit port override
      port = `:${wsPortOverride}`;
    } else if (window.location.port && window.location.port !== CLIENT.HTTP_STANDARD_PORT && window.location.port !== CLIENT.HTTPS_STANDARD_PORT) {
      // Development mode: use port 10051 if HTTP is on a non-standard port (like 10050)
      port = `:${CLIENT.DEFAULT_WS_PORT}`;
    } else {
      // Production with reverse proxy: use same port as HTTP (no port suffix needed)
      port = '';
    }

    const wsUrl = `${wsProtocol}//${window.location.hostname}${port}/ws`;
    console.log(`WebSocket URL: ${wsUrl}`);
    return wsUrl;
  }

  /**
   * Handle binary WebSocket messages
   * Binary message format:
   * - Byte 0: Message type (0x01 = DOSBox audio, 0x02 = pixel delta, 0x03 = character delta)
   * - Remaining bytes: Type-specific data
   */
  private handleBinaryMessage(buffer: ArrayBuffer): void {
    if (buffer.byteLength < 1) return;

    const view = new DataView(buffer);
    const messageType = view.getUint8(0);

    switch (messageType) {
      case 0x01: // DOSBox audio
        this.handleBinaryDOSBoxAudio(buffer);
        break;
      case 0x02: // Pixel delta
        this.handleBinaryPixelDelta(buffer);
        break;
      case 0x03: // Character delta (binary-encoded terminal cell changes)
        this.handleBinaryCharDelta(buffer);
        break;
      default:
        console.warn('Unknown binary message type:', messageType);
    }
  }

  /**
   * Handle binary character delta message
   * Format:
   *   [0x03] [cursorX: u8] [cursorY: u8] [cursorVisible: u8]
   *   [changeCount: u16LE]
   *   For each change: [row: u8] [col: u8] [charCode: u16LE] [fgBg: u8] [flags: u8]
   */
  private handleBinaryCharDelta(buffer: ArrayBuffer): void {
    if (!this.powerOn) return;
    if (buffer.byteLength < 6) return; // Minimum: header(1) + cursor(3) + count(2)

    const view = new DataView(buffer);
    const cursorX = view.getUint8(1);
    const cursorY = view.getUint8(2);
    const cursorVisible = view.getUint8(3) !== 0;
    const changeCount = view.getUint16(4, true); // Little-endian

    const HEADER_SIZE = 6;
    const CHANGE_SIZE = 6;

    if (buffer.byteLength < HEADER_SIZE + changeCount * CHANGE_SIZE) return;

    const bytes = new Uint8Array(buffer);
    const changes: CellChange[] = [];
    let offset = HEADER_SIZE;

    for (let i = 0; i < changeCount; i++) {
      const r = bytes[offset];
      const c = bytes[offset + 1];
      const charCode = view.getUint16(offset + 2, true); // u16LE — supports Unicode
      const fgBg = bytes[offset + 4];
      const flags = bytes[offset + 5];
      offset += CHANGE_SIZE;

      const fg = fgBg & 0x0F;
      const bg = (fgBg >> 4) & 0x0F;
      const ch = String.fromCharCode(charCode);

      const change: CellChange = { r, c, ch, fg, bg };
      if (flags & 1) change.bl = true;
      if (flags & 2) change.bo = true;
      changes.push(change);

      // Update local screen buffer
      if (r >= 0 && r < this.screenHeight && c >= 0 && c < this.screenWidth) {
        const cell: MinimalCell = { ch, fg, bg };
        if (change.bl) cell.bl = true;
        if (change.bo) cell.bo = true;
        this.screenBuffer[r][c] = cell;
      }
    }

    // Apply to renderer
    const cursor = { x: cursorX, y: cursorY, visible: cursorVisible };
    this.renderer?.applyDelta(changes, cursor);
  }

  /**
   * Handle binary pixel delta message
   * Format: [type:1][count:2][block1:68][block2:68]...
   * Each block: [x:2][y:2][pixels:64]
   */
  private handleBinaryPixelDelta(buffer: ArrayBuffer): void {
    if (!this.inPixelMode || !this.pixelRenderer) return;
    if (buffer.byteLength < 3) return;

    const view = new DataView(buffer);
    const blockCount = view.getUint16(1, true); // Little-endian

    const BLOCK_SIZE = 8;
    const BLOCK_HEADER_SIZE = 4; // x(2) + y(2)
    const BLOCK_DATA_SIZE = BLOCK_SIZE * BLOCK_SIZE; // 64 bytes
    const BLOCK_TOTAL_SIZE = BLOCK_HEADER_SIZE + BLOCK_DATA_SIZE; // 68 bytes

    const regions: PixelRegion[] = [];
    let offset = 3;

    for (let i = 0; i < blockCount; i++) {
      if (offset + BLOCK_TOTAL_SIZE > buffer.byteLength) break;

      const x = view.getUint16(offset, true);
      const y = view.getUint16(offset + 2, true);
      offset += 4;

      // Extract pixel data as raw Uint8Array (renderer accepts both string and Uint8Array)
      // Copy the data since the underlying buffer may be reused
      const pixelData = new Uint8Array(buffer, offset, BLOCK_DATA_SIZE);
      const pixelDataCopy = new Uint8Array(BLOCK_DATA_SIZE);
      pixelDataCopy.set(pixelData);
      offset += BLOCK_DATA_SIZE;

      regions.push({
        x,
        y,
        w: BLOCK_SIZE,
        h: BLOCK_SIZE,
        data: pixelDataCopy,  // Pass raw bytes directly, skip base64 encoding/decoding
      });
    }

    if (regions.length > 0) {
      this.pixelRenderer.applyDelta(regions);
    }
  }

  /**
   * Handle binary DOSBox audio message
   * Format: [type:1][rate:4][samples:N*4]
   */
  private handleBinaryDOSBoxAudio(buffer: ArrayBuffer): void {
    if (buffer.byteLength < 9) return; // Need at least 1 sample (5 + 4 bytes)

    const view = new DataView(buffer);
    const sampleRate = view.getUint32(1, true); // Little-endian

    // Bulk copy audio data to properly aligned buffer (Float32Array requires 4-byte alignment)
    // Offset 5 is not 4-byte aligned, so we must copy to an aligned ArrayBuffer
    const audioDataLength = buffer.byteLength - 5;
    const sampleCount = Math.floor(audioDataLength / 4);
    if (sampleCount === 0) return;

    const sampleBytes = new Uint8Array(buffer, 5, sampleCount * 4);
    const alignedBuffer = new ArrayBuffer(sampleCount * 4);
    new Uint8Array(alignedBuffer).set(sampleBytes);
    const samples = new Float32Array(alignedBuffer);

    // Initialize DOSBox audio player on first message
    if (!this.dosboxAudio) {
      this.dosboxAudio = new StreamingAudioPlayer();
      this.initAudioOnInteraction();
      const ctx = this.audio.getContext();
      if (ctx) {
        this.dosboxAudio.init(ctx, this.audio.getMasterGainNode() || undefined);
      } else {
        return;
      }
    }

    this.dosboxAudio.pushSamples(samples, sampleRate);
  }

  /**
   * Handle incoming message
   */
  private handleMessage(data: string): void {
    try {
      const message = JSON.parse(data) as ServerMessage;

      switch (message.type) {
        case 'connection:accepted':
          this._clientId = message.clientId;
          break;

        case 'fullSync':
          this.handleFullSync(message);
          break;

        case 'delta':
          this.handleDelta(message);
          break;

        case 'audio':
          this.handleAudio(message);
          break;

        case 'powerState':
          this.handlePowerState(message);
          break;

        case 'crtLevel':
          this.handleCRTLevel(message);
          break;

        case 'crtEvent':
          this.handleCRTEvent(message as import('../shared/protocol.js').CRTEventMessage);
          break;

        case 'ledState':
          this.handleLEDState(message);
          break;

        case 'pong':
          // Heartbeat received
          break;

        case 'error':
          console.error('Server error:', message.message);
          break;

        case 'pixel_mode':
          this.handlePixelMode(message);
          break;

        case 'pixel_delta':
          this.handlePixelDelta(message);
          break;

        case 'pixel_full_sync':
          this.handlePixelFullSync(message);
          break;

        case 'hybrid_mode':
          this.handleHybridMode(message);
          break;

        case 'hybrid_pixel_delta':
          this.handleHybridPixelDelta(message);
          break;

        case 'hybrid_pixel_full_sync':
          this.handleHybridPixelFullSync(message);
          break;

        case 'opl:mode':
          this.handleOPLMode(message);
          break;

        case 'opl:data':
          this.handleOPLData(message);
          break;

        case 'adpcm:mode':
          this.handleADPCMMode(message);
          break;

        case 'adpcm:data':
          this.handleADPCMData(message);
          break;

        case 'dosbox:audio':
          this.handleDOSBoxAudio(message as DOSBoxAudioMessage);
          break;
      }
    } catch (error) {
      console.error('Failed to parse message:', error);
    }
  }

  /**
   * Handle full sync message
   */
  private handleFullSync(message: FullSyncMessage): void {
    // Apply power state first
    this.powerOn = message.state.powerOn;

    // Get the terminal element
    const terminalElement = document.getElementById('terminal');

    // Handle power off state
    if (!this.powerOn) {
      // On initial sync, show off screen immediately without animation
      if (!this.initialSyncDone) {
        this.powerOffScreen.classList.add('active', 'immediate');
      } else {
        this.powerOffScreen.classList.remove('immediate');
        this.powerOffScreen.classList.add('active');
      }
      if (terminalElement) {
        terminalElement.style.visibility = 'hidden';
      }
    } else {
      this.powerOffScreen.classList.remove('active', 'immediate');
      if (terminalElement) {
        terminalElement.style.visibility = 'visible';
      }
    }

    // Mark initial sync as done
    this.initialSyncDone = true;

    // Only render if power is on
    if (this.powerOn) {
      // Decompress and apply screen
      this.applyCompressedScreen(message.screen.cells);

      // Convert screen buffer to Cell format for renderer
      const cells: Cell[][] = this.screenBuffer.map(row =>
        row.map(cell => {
          const c: Cell = {
            ch: cell.ch,
            fg: cell.fg,
            bg: cell.bg,
          };
          if (cell.bl !== undefined) c.bl = cell.bl;
          if (cell.bo !== undefined) c.bo = cell.bo;
          return c;
        })
      );

      // Render full screen
      this.renderer?.renderFullScreen(cells, {
        x: message.screen.cursorX,
        y: message.screen.cursorY,
        visible: message.screen.cursorVisible,
      });
    }

    // Apply other state
    this.crtDecay = message.state.crtDecay;
    this.updateCRTEffects();

    // Update LEDs
    this.setLED('power', message.state.led.power);
    this.setLED('hdd', message.state.led.hdd);
    this.setLED('network', message.state.led.network);
  }

  /**
   * Handle delta message
   */
  private handleDelta(message: DeltaMessage): void {
    // Skip rendering if power is off
    if (!this.powerOn) {
      return;
    }

    // Update local buffer
    for (const change of message.changes) {
      if (change.r >= 0 && change.r < this.screenHeight &&
          change.c >= 0 && change.c < this.screenWidth) {
        const cell: MinimalCell = {
          ch: change.ch,
          fg: change.fg,
          bg: change.bg,
        };
        if (change.bl !== undefined) cell.bl = change.bl;
        if (change.bo !== undefined) cell.bo = change.bo;
        this.screenBuffer[change.r][change.c] = cell;
      } else {
        console.warn(`Delta change out of bounds: row=${change.r}, col=${change.c}`);
      }
    }

    // Apply delta to renderer
    this.renderer?.applyDelta(message.changes, message.cursor);
  }

  /**
   * Apply compressed screen data
   */
  private applyCompressedScreen(cells: CompressedCells): void {
    let x = 0;
    let y = 0;

    for (const run of cells) {
      for (let i = 0; i < run.count; i++) {
        if (y >= this.screenHeight) break;

        this.screenBuffer[y][x] = { ...run.cell };
        x++;
        if (x >= this.screenWidth) {
          x = 0;
          y++;
        }
      }
    }
  }

  /**
   * Handle audio message
   */
  private handleAudio(message: AudioMessage): void {
    this.playSound(message.sound, message.volume, message.loop);
  }

  /**
   * Handle power state message
   */
  private handlePowerState(message: PowerStateMessage): void {
    this.powerOn = message.on;

    // Get the terminal element and CRT container
    const terminalElement = document.getElementById('terminal');
    const container = document.querySelector('.crt-container') || document.body;

    if (!message.on) {
      // Remove immediate class so animation plays (this is a real power-off action)
      this.powerOffScreen.classList.remove('immediate');
      this.powerOffScreen.classList.add('active');
      this.setLED('power', false);
      // Hide the terminal canvas when power is off
      if (terminalElement) {
        terminalElement.style.visibility = 'hidden';
      }
      // Pause cursor blink when power is off (Issue 20)
      this.renderer?.pauseCursorBlink();
      // Pause CRT animations when power is off
      container.classList.add('crt-paused');
    } else {
      this.powerOffScreen.classList.remove('active', 'immediate');
      this.setLED('power', true);
      // Show the terminal canvas when power is on
      if (terminalElement) {
        terminalElement.style.visibility = 'visible';
      }
      // Resume cursor blink when power is on (Issue 20)
      this.renderer?.resumeCursorBlink();
      // Resume CRT animations when power is on (only if tab is visible)
      if (!document.hidden) {
        container.classList.remove('crt-paused');
      }

      if (message.bootSequence) {
        this.playSound('boot');
      }
    }
  }

  /**
   * Handle CRT level message
   */
  private handleCRTLevel(message: CRTLevelMessage): void {
    this.crtDecay = message.level;
    this.updateCRTEffects(message.crtParams);
  }

  /**
   * Handle CRT one-shot event
   */
  private handleCRTEvent(message: import('../shared/protocol.js').CRTEventMessage): void {
    const container = this.screenContainer;
    const className = `crt-event-${message.event}`;

    // Remove any existing event animation
    container.classList.remove(className);
    // Force reflow to restart animation
    void container.offsetWidth;
    container.classList.add(className);

    // Set intensity and duration as CSS custom properties
    container.style.setProperty('--crt-event-intensity', String(message.intensity));
    container.style.setProperty('--crt-event-duration', `${message.duration}ms`);

    // Remove class after animation completes
    setTimeout(() => {
      container.classList.remove(className);
    }, message.duration);
  }

  /**
   * Handle LED state message
   */
  private handleLEDState(message: LEDStateMessage): void {
    this.setLED('power', message.power);
    this.setLED('hdd', message.hdd);
    this.setLED('network', message.network);
  }

  /**
   * Handle pixel mode toggle
   */
  private handlePixelMode(message: PixelModeMessage): void {
    this.inPixelMode = message.enabled;

    if (message.enabled) {
      // Switch to pixel mode
      this.pixelRenderer?.show();
      // Update pixel canvas size to match terminal
      if (this.renderer && this.pixelRenderer) {
        this.pixelRenderer.updateScale(this.renderer.getCanvas());
      }
    } else {
      // Switch back to character mode
      this.pixelRenderer?.hide();
      // Release pointer lock when exiting pixel mode
      if (document.pointerLockElement) {
        document.exitPointerLock();
      }
      this.pointerLocked = false;
    }
  }

  /**
   * Handle pixel delta update
   */
  private handlePixelDelta(message: PixelDeltaMessage): void {
    if (!this.inPixelMode || !this.pixelRenderer) return;
    this.pixelRenderer.applyDelta(message.regions);
  }

  /**
   * Handle pixel full sync
   */
  private handlePixelFullSync(message: PixelFullSyncMessage): void {
    if (!this.pixelRenderer) return;
    this.pixelRenderer.applyFullSync(message.width, message.height, message.data);
  }

  /**
   * Handle hybrid mode toggle
   */
  private handleHybridMode(message: HybridModeMessage): void {
    this.inHybridMode = message.enabled;

    if (this.hybridRenderer) {
      this.hybridRenderer.setHybridMode(message.enabled, message.regions);
      // Update scale to match terminal
      if (message.enabled && this.renderer) {
        this.hybridRenderer.updateScale(this.renderer.getCanvas());
      }
    }
  }

  /**
   * Handle hybrid pixel delta update
   */
  private handleHybridPixelDelta(message: HybridPixelDeltaMessage): void {
    if (!this.inHybridMode || !this.hybridRenderer) return;
    this.hybridRenderer.updateRegion(message.regionId, message.regions);
  }

  /**
   * Handle hybrid pixel full sync
   */
  private handleHybridPixelFullSync(message: HybridPixelFullSyncMessage): void {
    if (!this.hybridRenderer) return;
    this.hybridRenderer.fullSyncRegion(message.regionId, message.width, message.height, message.data);
  }

  /**
   * Handle OPL mode enable/disable
   */
  private handleOPLMode(message: OPLModeMessage): void {
    console.log('OPL mode message received:', message.enabled);
    if (message.enabled) {
      // Initialize OPL synth if not already done
      if (!this.oplSynth) {
        // Initialize audio first if needed
        this.initAudioOnInteraction();
        const ctx = this.audio.getContext();
        console.log('Audio context:', ctx ? 'available' : 'not available', ctx?.state);
        if (ctx) {
          this.oplSynth = createOPL2Synthesizer(ctx, this.audio.getMasterGainNode() || undefined);
          console.log('OPL2 synthesizer initialized');
        } else {
          console.warn('Cannot create OPL synth - no audio context');
        }
      }
      this.oplSynth?.setEnabled(true);
      console.log('OPL synth enabled:', this.oplSynth?.isEnabled());
    } else {
      this.oplSynth?.setEnabled(false);
      this.oplSynth?.stopAll();
      console.log('OPL synth disabled');
    }
  }

  /**
   * Handle OPL audio data
   */
  private handleOPLData(message: OPLDataMessage): void {
    if (!this.oplSynth) {
      // Try to initialize on first data if mode was missed
      if (!this.oplSynth) {
        this.initAudioOnInteraction();
        const ctx = this.audio.getContext();
        if (ctx) {
          this.oplSynth = createOPL2Synthesizer(ctx, this.audio.getMasterGainNode() || undefined);
          this.oplSynth.setEnabled(true);
          console.log('OPL2 synthesizer initialized on first data');
        }
      }
      if (!this.oplSynth) return;
    }

    // Decode base64 data to ArrayBuffer
    try {
      const binaryString = atob(message.data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      this.oplSynth.updateFromBinary(bytes.buffer);
    } catch (error) {
      console.error('Failed to decode OPL data:', error);
    }
  }

  /**
   * Handle ADPCM audio mode enable/disable
   */
  private handleADPCMMode(message: ADPCMModeMessage): void {
    console.log('ADPCM mode message received:', message.enabled);
    if (message.enabled) {
      // Initialize ADPCM player if not already done
      if (!this.adpcmPlayer) {
        this.initAudioOnInteraction();
        const ctx = this.audio.getContext();
        console.log('Audio context for ADPCM:', ctx ? 'available' : 'not available', ctx?.state);
        if (ctx) {
          this.adpcmPlayer = createADPCMPlayer({
            sampleRate: ADPCM.SAMPLE_RATE,  // Retro quality
            bufferSize: ADPCM.BUFFER_SIZE,  // Buffer 3 packets before playing
            retroFilter: true,              // Apply low-pass filter
            volume: ADPCM.VOLUME,
          });
          this.adpcmPlayer.initialize(ctx, this.audio.getMasterGainNode() || undefined);
          console.log('ADPCM player initialized');
        } else {
          console.warn('Cannot create ADPCM player - no audio context');
        }
      }
    } else {
      // Stop and disable ADPCM playback
      this.adpcmPlayer?.stop();
      console.log('ADPCM player stopped');
    }
  }

  /**
   * Handle ADPCM audio data packet
   */
  private handleADPCMData(message: ADPCMDataMessage): void {
    // Initialize player on first data if mode message was missed
    if (!this.adpcmPlayer) {
      this.initAudioOnInteraction();
      const ctx = this.audio.getContext();
      if (ctx) {
        this.adpcmPlayer = createADPCMPlayer({
          sampleRate: ADPCM.SAMPLE_RATE,
          bufferSize: ADPCM.BUFFER_SIZE,
          retroFilter: true,
          volume: ADPCM.VOLUME,
        });
        this.adpcmPlayer.initialize(ctx, this.audio.getMasterGainNode() || undefined);
        console.log('ADPCM player initialized on first data');
      }
      if (!this.adpcmPlayer) return;
    }

    // Decode base64 data to Uint8Array and send to player
    try {
      const binaryString = atob(message.data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      this.adpcmPlayer.receivePacket(bytes);
    } catch (error) {
      console.error('Failed to decode ADPCM data:', error);
    }
  }

  /**
   * Handle DOSBox audio data from js-dos emulation (JSON fallback)
   * Note: Binary WebSocket is now the preferred method - see handleBinaryDOSBoxAudio
   * This handler remains for backward compatibility with older server versions.
   */
  private handleDOSBoxAudio(message: DOSBoxAudioMessage): void {
    // Initialize DOSBox audio player on first message
    if (!this.dosboxAudio) {
      this.dosboxAudio = new StreamingAudioPlayer();
      this.initAudioOnInteraction();
      const ctx = this.audio.getContext();
      if (ctx) {
        this.dosboxAudio.init(ctx, this.audio.getMasterGainNode() || undefined);
        console.log('DOSBox audio player initialized (JSON fallback)');
      } else {
        console.warn('Cannot initialize DOSBox audio - no audio context');
        return;
      }
    }

    // Decode Base64 to Float32Array
    try {
      const binaryString = atob(message.data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const samples = new Float32Array(bytes.buffer);
      this.dosboxAudio.pushSamples(samples, message.rate);
    } catch (error) {
      console.error('Failed to decode DOSBox audio data:', error);
    }
  }

  /**
   * Update CRT decay effects using CSS custom properties for smooth continuous effects.
   * Falls back to legacy decay classes when no crtParams are provided.
   */
  private updateCRTEffects(crtParams?: import('../shared/protocol.js').CRTEffectParams): void {
    const container = this.screenContainer;

    if (crtParams) {
      // New continuous mode: set CSS custom properties
      container.classList.remove('decay-1', 'decay-2', 'decay-3');
      container.classList.add('crt-continuous');

      const s = container.style;
      s.setProperty('--crt-scanline-opacity', String(crtParams.scanlineOpacity));
      s.setProperty('--crt-scanline-speed', `${crtParams.scanlineSpeed}s`);
      s.setProperty('--crt-flicker-opacity', String(crtParams.flickerOpacity));
      s.setProperty('--crt-flicker-speed', `${crtParams.flickerSpeed}s`);
      s.setProperty('--crt-noise-opacity', String(crtParams.noiseOpacity));
      s.setProperty('--crt-vignette-strength', String(crtParams.vignetteStrength));
      s.setProperty('--crt-phosphor-glow', `${crtParams.phosphorGlow}px`);
      s.setProperty('--crt-burn-in-opacity', String(crtParams.burnInOpacity));
      s.setProperty('--crt-color-saturation', String(crtParams.colorSaturation));
      s.setProperty('--crt-convergence-offset', `${crtParams.convergenceOffset}px`);
      s.setProperty('--crt-focus-blur', `${crtParams.focusBlur}px`);
      s.setProperty('--crt-brightness-amplitude', String(crtParams.brightnessAmplitude));
      s.setProperty('--crt-jitter-amplitude', `${crtParams.jitterAmplitude}px`);
      s.setProperty('--crt-wave-amplitude', `${crtParams.waveAmplitude}px`);
      s.setProperty('--crt-wave-frequency', String(crtParams.waveFrequency));
      s.setProperty('--crt-color-fringe', String(crtParams.colorFringeIntensity));
      s.setProperty('--crt-brightness-pump-speed', `${crtParams.brightnessPumpSpeed}s`);
      s.setProperty('--crt-color-temp-shift', `${crtParams.colorTempShift}deg`);
      s.setProperty('--crt-uneven-brightness', String(crtParams.unevenBrightness));
    } else {
      // Legacy mode: use decay classes
      container.classList.remove('decay-1', 'decay-2', 'decay-3', 'crt-continuous');

      if (this.crtDecay >= CRT_DECAY.LEVEL_3_THRESHOLD) {
        container.classList.add('decay-3');
      } else if (this.crtDecay >= CRT_DECAY.LEVEL_2_THRESHOLD) {
        container.classList.add('decay-2');
      } else if (this.crtDecay >= CRT_DECAY.LEVEL_1_THRESHOLD) {
        container.classList.add('decay-1');
      }
    }
  }

  /**
   * Set LED state
   */
  private setLED(led: 'power' | 'hdd' | 'network', on: boolean): void {
    const element = led === 'power' ? this.ledPower :
                    led === 'hdd' ? this.ledHdd : this.ledNetwork;

    if (on) {
      element.classList.add('on');
    } else {
      element.classList.remove('on');
    }
  }

  /**
   * Play sound effect using Web Audio engine
   */
  private playSound(sound: string, volume?: number, _loop: boolean = false): void {
    // Only set volume if explicitly provided
    if (volume !== undefined) {
      this.audio.setVolume(volume);
    }

    // Handle DTMF sounds specially (format: "dtmf:X" where X is the digit)
    if (sound.startsWith('dtmf:')) {
      const digit = sound.substring(5);
      this.audio.playDTMF(digit);
      return;
    }

    // Handle phone ring specially
    if (sound === 'phone-ring') {
      this.audio.playPhoneRing();
      return;
    }

    this.audio.play(sound);
  }

  /**
   * Send message to server
   */
  private send(message: ClientMessage): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    }
  }

  /**
   * Update connection status
   */
  private updateStatus(status: 'connecting' | 'connected' | 'disconnected'): void {
    this.statusElement.className = 'connection-status ' + status;
    const textElement = this.statusElement.querySelector('.status-text');

    if (!textElement) {
      console.warn('Status text element not found');
      return;
    }

    switch (status) {
      case 'connecting':
        textElement.textContent = 'Connecting...';
        break;
      case 'connected':
        textElement.textContent = 'Connected';
        break;
      case 'disconnected':
        textElement.textContent = 'Disconnected';
        break;
    }
  }

  /**
   * Start ping interval
   */
  private startPing(): void {
    this.pingInterval = setInterval(() => {
      this.send({ type: 'ping', clientTime: Date.now() });
    }, RECONNECT.PING_INTERVAL_MS);
  }

  /**
   * Stop ping interval
   */
  private stopPing(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  /**
   * Schedule reconnect
   */
  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('Max reconnect attempts reached');
      return;
    }

    const delay = Math.min(RECONNECT.BASE_DELAY_MS * Math.pow(2, this.reconnectAttempts), RECONNECT.MAX_DELAY_MS);
    this.reconnectAttempts++;

    this._reconnectTimeout = setTimeout(() => {
      console.log(`Reconnect attempt ${this.reconnectAttempts}`);
      this.connect();
    }, delay);
  }

  /**
   * Get renderer for external access (e.g., testing)
   */
  getRenderer(): TerminalRenderer | null {
    return this.renderer;
  }
}

// Export for window access (needed for E2E tests)
declare global {
  interface Window {
    terminalClient: TerminalClient;
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', async () => {
  const container = document.getElementById('terminal');
  if (container) {
    const client = new TerminalClient();
    window.terminalClient = client;
    await client.init(container);
  }
});
